import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Paper,
    Button,
    Alert,
    Snackbar,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Progress from "../../../components/progress/Progress";
import FloorService from "../../../services/floor/FloorService";
import UserService from "../../../services/user/UserService";
import {
    FormWrapper,
    EntryWrapper,
} from "../../../components/customStyles/form";
import FloorFormFields from "./FloorFormFields";
import IFloor, { initialFloorState } from "../../../models/floor";
import { ERole } from "../../../models/enums/role";
import buildingService from "../../../services/building/BuildingService";
import IAdminPageBuildingDetail from "../../../models/adminPage/adminPageBuildingDetail";
import { convertToSVG } from "../../../utils/convertToSVG";
import EditOnly from "../../../components/permissions/EditOnly";

const SUCCESS_STATUS_CODE = 200;

const FloorAddPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [isErrorMessageVisible, setIsErrorMessageVisible] = useState(false);

    const [floorState, setFloorState] =
        useState<IFloor>(initialFloorState);
    const [buildings, setBuildings] = useState<IAdminPageBuildingDetail[]>([]);
    const [superAdminsIds, setSuperAdminsIds] = useState<number[]>([]);

    const [hbscanMapFile, setHbscanMapFile] = useState<File>();
    const [hbscanMapURL, setHbscanMapURL] = useState('');

    const [isFormSending, setIsFormSending] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");

    const onSnackbarClose = () => {
        setSnackbarOpen(false);
    };


    useEffect(() => {
        buildingService.GetBuildingsDetails().then(setBuildings);

        UserService.GetUsersByCompany().then((users) => {
            if (!users) return;
            const superAdmins = users.filter((user) => user.role === ERole.SuperAdmin).map((superAdmin) => superAdmin.id);
            setSuperAdminsIds(superAdmins);
        });
    }, []);


    const onCreateFloor = (event: FormEvent) => {
        event.preventDefault();

        setIsFormSending(true);

        const floor = new FormData();

        floor.append('id', floorState.id.toString());
        floor.append('name', floorState.name);
        floor.append('buildingId', floorState.buildingId.toString());
        floor.append('floorNumber', floorState.floorNumber.toString());

        if (floorState.managerIds) {
            const managerIds = [...floorState.managerIds, ...superAdminsIds];

            managerIds.forEach(managerId => {
                floor.append('managerIds[]', managerId.toString());
            });
        }

        if (hbscanMapFile) floor.append('hbscanMap', hbscanMapFile);

        FloorService.PostFloor(floor)
            .then((response) => {
                if (response.status === SUCCESS_STATUS_CODE) {
                    navigate("..");
                    return;
                }
                setIsErrorMessageVisible(true);
            })
            .finally(() => setIsFormSending(false));
    };

    const createMapURL = (map: Blob | File) => {
        const url = URL.createObjectURL(map);
        setHbscanMapURL(url);
        return () => URL.revokeObjectURL(url);
    }

    const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0]) return;

        const file = e.target.files[0];

        const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];

        if (!allowedTypes.includes(file.type)) {

            setSnackbarMessage(t("table.floor.onlyPictureAllowed"));
            setSnackbarOpen(true);
            e.target.value = '';
            return;
        }

        try {
            const svgFile = await convertToSVG(file);

            setHbscanMapFile(svgFile);
            createMapURL(svgFile);
        } catch (error) {
            setSnackbarMessage(t("table.floor.errorConvert"));
            setSnackbarOpen(true);
        }

        e.target.value = '';
    };

    const onRemoveFile = () => {
        setHbscanMapFile(undefined);
        setHbscanMapURL('');
    }

    return (
        <Box component={Paper} sx={{ width: "100%" }}>
            <form autoComplete="off" onSubmit={onCreateFloor}>
                <FormWrapper>
                    {isErrorMessageVisible && (
                        <Alert
                            severity="error"
                            sx={{ marginBottom: "1em" }}
                        >
                            {t("table.floor.addErrorMessage")}
                        </Alert>
                    )}

                    <FloorFormFields
                        floorState={floorState}
                        setFloorState={setFloorState}
                        hbscanMapURL={hbscanMapURL}
                        onFileChange={onFileChange}
                        onRemoveFile={onRemoveFile}
                        buildings={buildings}
                    />

                    <EntryWrapper
                        sx={{
                            justifyContent: "flex-end",
                            marginBlock: "1em",
                        }}
                    >
                        {(
                            <EntryWrapper
                                sx={{ justifyContent: "flex-end" }}
                            >
                                <Progress isLoading={isFormSending}>
                                    <EditOnly>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="secondary"
                                            disabled={
                                                !floorState.name.trim() ||
                                                floorState.floorNumber === null ||
                                                floorState.floorNumber === undefined ||
                                                !floorState.buildingId
                                            }
                                            sx={{
                                                marginTop: "4em",
                                            }}
                                        >
                                            {t("table.createFloor")}
                                        </Button>
                                    </EditOnly>
                                </Progress>
                            </EntryWrapper>
                        )}
                    </EntryWrapper>
                </FormWrapper>
            </form>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={onSnackbarClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={onSnackbarClose}
                    severity="error"
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box >
    );
};

export default FloorAddPage;
